import React, { useState } from 'react';
import styled from 'styled-components';

interface Tab {
  key: string;
  title: string;
  content: React.ReactNode;
  disabled?: boolean;
}

interface TabContainerProps {
  tabs: Tab[];
  activeTab?: string;
  onChange?: (activeTab: string) => void;
  className?: string;
}

const Container = styled.div`
  /* No box styling - unified canvas */
`;

const TabHeader = styled.div`
  display: flex;
  gap: 4px;
  overflow-x: auto;

  @media (max-width: 767px) {
    &::-webkit-scrollbar {
      height: 4px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background: var(--border-color);
      border-radius: 2px;
    }
  }
`;

const TabButton = styled.button<{ $active: boolean; disabled?: boolean }>`
  padding: 12px 20px;
  background: none;
  border: none;
  font-size: 14px;
  font-weight: 500;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  color: ${props => {
    if (props.disabled) return 'var(--text-tertiary)';
    return props.$active ? 'var(--primary-color)' : 'var(--text-secondary)';
  }};
  border-bottom: 2px solid ${props => props.$active ? 'var(--primary-color)' : 'transparent'};
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover:not(:disabled) {
    color: var(--text-primary);
  }

  &:focus {
    outline: none;
  }

  @media (max-width: 767px) {
    padding: 10px 14px;
    font-size: 13px;
  }
`;

const Separator = styled.div`
  height: 1px;
  background: rgba(0, 0, 0, 0.06);
  margin: 0;
`;

const TabContent = styled.div`
  /* No padding - let tab content handle its own padding */
`;

const TabContainer: React.FC<TabContainerProps> = ({
  tabs,
  activeTab,
  onChange,
  className
}) => {
  const [internalActiveTab, setInternalActiveTab] = useState(tabs[0]?.key || '');
  
  const currentActiveTab = activeTab || internalActiveTab;
  
  const handleTabChange = (tabKey: string) => {
    const tab = tabs.find(t => t.key === tabKey);
    if (tab && !tab.disabled) {
      if (onChange) {
        onChange(tabKey);
      } else {
        setInternalActiveTab(tabKey);
      }
    }
  };
  
  const activeTabContent = tabs.find(tab => tab.key === currentActiveTab)?.content;
  
  return (
    <Container className={className}>
      <TabHeader>
        {tabs.map((tab) => (
          <TabButton
            key={tab.key}
            $active={tab.key === currentActiveTab}
            disabled={tab.disabled}
            onClick={() => handleTabChange(tab.key)}
          >
            {tab.title}
          </TabButton>
        ))}
      </TabHeader>

      <Separator />

      <TabContent>
        {activeTabContent}
      </TabContent>
    </Container>
  );
};

export default TabContainer;